
import { useCallback } from 'react';
import { usePerformanceTracking } from '@/hooks/usePerformanceTracking';
import { TopicType } from '@/types/assistant';
import { logger } from '@/utils/production-logger';

interface UseMessagePerformanceTrackerProps {
  userId: string | null;
}

interface MessagePerformanceSession {
  requestId: string;
  startTime: number;
  messageProcessingStart: number;
}

interface UseMessagePerformanceTrackerReturn {
  startMessagePerformance: (topic: TopicType) => MessagePerformanceSession;
  finishMessagePerformance: (
    session: MessagePerformanceSession,
    topic: TopicType,
    status: 'success' | 'error',
    errorMessage?: string,
    cached?: boolean
  ) => Promise<void>;
}

export const useMessagePerformanceTracker = ({
  userId
}: UseMessagePerformanceTrackerProps): UseMessagePerformanceTrackerReturn => {
  const { startTracking, finishTracking } = usePerformanceTracking();

  const startMessagePerformance = useCallback((topic: TopicType): MessagePerformanceSession => {
    const performanceMetrics = startTracking();
    
    logger.info('Starting message performance tracking', {
      requestId: performanceMetrics.requestId,
      userId,
      topic,
      component: 'useMessagePerformanceTracker'
    });

    return {
      requestId: performanceMetrics.requestId,
      startTime: performanceMetrics.requestStartTime,
      messageProcessingStart: performanceMetrics.messageProcessingStart
    };
  }, [startTracking, userId]);

  const finishMessagePerformance = useCallback(async (
    session: MessagePerformanceSession,
    topic: TopicType,
    status: 'success' | 'error',
    errorMessage?: string,
    cached: boolean = false
  ) => {
    if (!userId) {
      logger.warn('Cannot finish performance tracking without userId', {
        requestId: session.requestId,
        component: 'useMessagePerformanceTracker'
      });
      return;
    }

    const performanceMetrics = {
      requestId: session.requestId,
      requestStartTime: session.startTime,
      messageProcessingStart: session.messageProcessingStart
    };

    await finishTracking(
      performanceMetrics,
      userId,
      topic,
      status,
      errorMessage,
      cached
    );

    logger.debug('Message performance tracking completed', {
      requestId: session.requestId,
      topic,
      metadata: { status, cached, errorMessage },
      component: 'useMessagePerformanceTracker'
    });
  }, [finishTracking, userId]);

  return {
    startMessagePerformance,
    finishMessagePerformance
  };
};
